import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, AlertTriangle, ArrowLeftRight, Banknote, Boxes, Building2, CheckCircle2,
  ChevronDown, ChevronRight, Loader2, Package, PackageCheck, Pencil, Plus, Search,
  Trash2, Undo2, User, Warehouse, X,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import { useHrGetStaffQuery } from "../../hr/api/hrApi";
import { fmtQty, staffName } from "../utils/stores";
import MovementModal from "../components/MovementModal";
import StoreShell from "../components/StoreShell";
import StockActionModal, { type StockAction } from "../components/StockActionModal";
import {
  useStoreGetStoresQuery,
  useStoreGetStoreQuery,
  useStoreCreateStoreMutation,
  useStoreUpdateStoreMutation,
  useStoreDeleteStoreMutation,
  useStoreGetHoldingsQuery,
  useStoreGetReturnRequestsQuery,
  useStoreConfirmReturnMutation,
  useStoreRejectReturnMutation,
} from "../api/storesApi";
import { useStoreCustody } from "../utils/useStoreCustody";

type UiMsg = { type: "success" | "error"; text: string } | null;

const inputCls =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-xs font-medium text-gray-600 mb-1";

const naira = (n: number) =>
  `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

type StoreForm = {
  id?: number;
  name: string;
  code: string;
  location: string;
  custodian_admin_id: string;
  note: string;
  is_active: boolean;
  is_main: boolean;
};

const emptyForm: StoreForm = {
  name: "", code: "", location: "", custodian_admin_id: "", note: "",
  is_active: true, is_main: false,
};

// ── What one store is holding ────────────────────────────────────────────────

/**
 * The shelves of one store, fetched only when it is opened.
 *
 * A store's card answers "how much and what is it worth"; this answers "what
 * exactly", which is the question somebody standing in the room has. Loaded
 * lazily because most visits are to one store, not all of them.
 */
function StoreShelf({ storeId }: { storeId: number }) {
  const { data, isLoading } = useStoreGetStoreQuery(storeId);

  const rows = useMemo(() => {
    const balances = (data?.balances ?? []) as any[];
    return balances
      .filter((b) => Number(b.quantity) !== 0)
      .sort((a, b) => (a.item?.name ?? "").localeCompare(b.item?.name ?? ""));
  }, [data]);

  if (isLoading) {
    return (
      <div className="py-6 text-center text-xs text-gray-400 animate-pulse">Loading what is in here…</div>
    );
  }

  if (!rows.length) {
    return (
      <div className="py-6 text-center">
        <Package className="w-7 h-7 text-gray-200 mx-auto mb-2" />
        <p className="text-xs text-gray-400">This store is empty — nothing has been received into it yet.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-400 border-b">
            <th className="py-2 pr-3 font-medium text-xs uppercase tracking-wider">Item</th>
            <th className="py-2 px-3 font-medium text-xs uppercase tracking-wider text-right">Held here</th>
            <th className="py-2 pl-3 font-medium text-xs uppercase tracking-wider text-right">Value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((b: any) => {
            const qty = Number(b.quantity);
            const low = qty <= Number(b.item?.reorder_level ?? 0);
            return (
              <tr key={b.id} className={low ? "bg-amber-50/40" : undefined}>
                <td className="py-2.5 pr-3">
                  {/* Clicking through to the item is how you answer "what has
                      happened to this" — who holds it, who returned it, when. */}
                  <Link to={`/stores/items/${b.item?.id}`} className="group">
                    <div className="font-medium text-gray-900 group-hover:text-indigo-600">
                      {b.item?.name ?? "—"}
                    </div>
                    <div className="text-xs text-gray-400 font-mono">{b.item?.code}</div>
                  </Link>
                </td>
                <td className="py-2.5 px-3 text-right">
                  <span className={low ? "text-amber-600 font-semibold" : "text-gray-800"}>
                    {fmtQty(qty)} {b.item?.unit}
                  </span>
                  {low && (
                    <div className="text-[11px] text-amber-600 flex items-center gap-1 justify-end">
                      <AlertTriangle className="w-3 h-3" /> at reorder level
                    </div>
                  )}
                </td>
                <td className="py-2.5 pl-3 text-right text-gray-500">
                  {b.item?.unit_cost ? naira(qty * Number(b.item.unit_cost)) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── A tile ───────────────────────────────────────────────────────────────────

function Tile({
  icon, label, value, tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "amber";
}) {
  return (
    <div className={`rounded-xl border p-4 ${tone === "amber" ? "bg-amber-50 border-amber-100" : "bg-white"}`}>
      <div className="flex items-center gap-2 text-gray-400">
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-bold mt-1.5 ${tone === "amber" ? "text-amber-700" : "text-gray-900"}`}>
        {value}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function StoresPageInner() {
  const { data: stores = [], isLoading, error } = useStoreGetStoresQuery();
  const { data: holdings = [] } = useStoreGetHoldingsQuery();
  const { data: pendingReturns = [] } = useStoreGetReturnRequestsQuery({ status: "pending" });
  const { data: staffData } = useHrGetStaffQuery({});
  const [confirmReturn, { isLoading: confirming }] = useStoreConfirmReturnMutation();
  const [rejectReturn, { isLoading: rejecting }] = useStoreRejectReturnMutation();

  // A store is somebody's. The buttons follow that, so nobody is offered an
  // action the API will refuse.
  const { canWork, refusal, isSuperAdmin } = useStoreCustody();
  const [create, { isLoading: creating }] = useStoreCreateStoreMutation();
  const [update, { isLoading: updating }] = useStoreUpdateStoreMutation();
  const [remove] = useStoreDeleteStoreMutation();

  const [msg, setMsg] = useState<UiMsg>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<StoreForm>(emptyForm);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [receiving, setReceiving] = useState<number | null>(null);
  const [action, setAction] = useState<{ action: StockAction; storeId: number } | null>(null);

  const staff = useMemo(() => (staffData as any)?.rows ?? (staffData as any)?.data ?? [], [staffData]);

  const totals = useMemo(() => {
    const active = stores.filter((s: any) => s.is_active).length;
    const units = stores.reduce((n: number, s: any) => n + Number(s.total_quantity ?? 0), 0);
    const value = stores.reduce((n: number, s: any) => n + Number(s.stock_value ?? 0), 0);
    const low = stores.reduce((n: number, s: any) => n + Number(s.low_stock_count ?? 0), 0);
    const withStaff = (holdings as any[]).reduce((n, h) => n + Number(h.quantity ?? 0), 0);
    return { active, units, value, low, withStaff };
  }, [stores, holdings]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s: any) =>
      [s.name, s.code, s.location, staffName(s.custodian)].filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [stores, search]);

  const flash = (m: UiMsg) => {
    setMsg(m);
    if (m) setTimeout(() => setMsg(null), 6000);
  };

  const openCreate = () => { setForm(emptyForm); setShowForm(true); };

  const openEdit = (s: any) => {
    setForm({
      id: s.id,
      name: s.name ?? "",
      code: s.code ?? "",
      location: s.location ?? "",
      custodian_admin_id: s.custodian_admin_id ?? "",
      note: s.note ?? "",
      is_active: !!s.is_active,
      is_main: !!s.is_main,
    });
    setShowForm(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    const body = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      location: form.location.trim() || null,
      custodian_admin_id: form.custodian_admin_id || null,
      note: form.note.trim() || null,
      is_active: form.is_active,
      is_main: form.is_main,
    };

    try {
      if (form.id) {
        await update({ id: form.id, body }).unwrap();
        flash({ type: "success", text: "Store updated." });
      } else {
        await create(body).unwrap();
        flash({ type: "success", text: "Store created." });
      }
      setShowForm(false);
    } catch (err) {
      flash({ type: "error", text: describeApiError(err, "Could not save that store.") });
    }
  };

  const onDelete = async (s: any) => {
    if (!window.confirm(`Delete "${s.name}"?\n\nOnly possible while it is empty and nothing has ever moved through it.`)) return;
    try {
      await remove(s.id).unwrap();
      flash({ type: "success", text: "Store deleted." });
    } catch (err) {
      flash({ type: "error", text: describeApiError(err, "Could not delete that store.") });
    }
  };

  return (
    <StoreShell>
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white border rounded-xl p-5 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-50 border border-indigo-100">
              <Warehouse className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Stores</h1>
              <p className="text-sm text-gray-500">
                Every place the Institute keeps stock, what is on its shelves, and what is
                out with staff.
              </p>
            </div>
          </div>

          {/* Creating a store and naming its custodian is how access to stock
              is handed out, so it does not sit with the people it is handed
              to — super admin only, matching the API. */}
          {isSuperAdmin && (
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg"
            >
              <Plus className="w-4 h-4" /> New store
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <Tile icon={<Building2 className="w-3.5 h-3.5" />} label="Stores" value={String(totals.active)} />
          <Tile icon={<Boxes className="w-3.5 h-3.5" />} label="Units on shelves" value={fmtQty(totals.units)} />
          <Tile icon={<Banknote className="w-3.5 h-3.5" />} label="Stock value" value={naira(totals.value)} />
          <Tile
            icon={<PackageCheck className="w-3.5 h-3.5" />}
            label="Out with staff"
            value={fmtQty(totals.withStaff)}
          />
        </div>

        {totals.low > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {totals.low} item {totals.low === 1 ? "line is" : "lines are"} at or below the reorder level
            across the stores.
          </div>
        )}

        {stores.length > 3 && (
          <div className="relative mt-4">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a store by name, code, location or custodian…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
        )}
      </div>

      {msg && (
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 text-sm font-medium ${
            msg.type === "success"
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span className="leading-relaxed">{msg.text}</span>
        </div>
      )}

      {isLoading && <div className="py-14 text-center text-sm text-gray-400 animate-pulse">Loading stores…</div>}

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          {describeApiError(error, "Could not load the stores.")}
        </div>
      )}

      {!isLoading && !error && stores.length === 0 && (
        <div className="bg-white border rounded-xl py-16 text-center px-6">
          <Building2 className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          {/* Two different situations, and telling them apart matters: there
              may be no stores at all, or none of them may be yours. */}
          <p className="text-sm font-medium text-gray-600">
            {isSuperAdmin ? "No stores yet" : "No store is assigned to you"}
          </p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
            {isSuperAdmin
              ? "Create one for each place stock is kept. Items are then received into a store, moved between stores, and issued from a store to a member of staff."
              : "You can only see the stores you are custodian of. Ask a super admin to assign you one."}
          </p>
        </div>
      )}

      {/* Returns waiting on a store — the custodian's inbox. Nothing has moved
          while one of these is pending. */}
      {(pendingReturns as any[]).filter((r) => canWork(r.store)).length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Undo2 className="w-4 h-4 text-amber-600" />
            <h2 className="font-bold text-gray-800">Returns waiting on you</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Staff have asked to hand these back. They are still recorded as holding the stock
            until you confirm you have it.
          </p>

          <div className="space-y-2">
            {(pendingReturns as any[]).filter((r) => canWork(r.store)).map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {staffName(r.admin)} → {r.store?.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {fmtQty(r.quantity)} {r.item?.unit} of {r.item?.name}
                    {r.reference ? ` · Ref ${r.reference}` : ""}
                  </div>
                  {r.note && <div className="text-xs text-gray-400 mt-0.5">{r.note}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={async () => {
                      try {
                        const res: any = await confirmReturn({ id: r.id }).unwrap();
                        flash({ type: "success", text: res?.message || "Confirmed." });
                      } catch (err) {
                        flash({ type: "error", text: describeApiError(err, "Could not confirm that return.") });
                      }
                    }}
                    disabled={confirming}
                    className="px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-black text-white text-xs font-medium disabled:opacity-40"
                  >
                    Confirm received
                  </button>
                  <button
                    onClick={async () => {
                      const why = window.prompt("Say why it is not being accepted (optional):") ?? undefined;
                      try {
                        const res: any = await rejectReturn({ id: r.id, decision_note: why }).unwrap();
                        flash({ type: "success", text: res?.message || "Marked as not received." });
                      } catch (err) {
                        flash({ type: "error", text: describeApiError(err, "Could not update that return.") });
                      }
                    }}
                    disabled={rejecting}
                    className="px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Not received
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The stores */}
      <div className="space-y-4">
        {visible.map((s: any) => {
          const isOpen = !!open[s.id];
          const mine = canWork(s);

          return (
            <div key={s.id} className="bg-white border rounded-xl overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`p-2.5 rounded-xl border ${s.is_active ? "bg-gray-900 border-gray-900" : "bg-gray-100 border-gray-200"}`}>
                      <Warehouse className={`w-4 h-4 ${s.is_active ? "text-white" : "text-gray-400"}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-900">{s.name}</span>
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg bg-gray-100 text-gray-500 font-mono">
                          {s.code}
                        </span>
                        {/* The hub: stock reaches every other store through it. */}
                        {s.is_main && (
                          <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700">
                            Main store
                          </span>
                        )}
                        {!s.is_active && (
                          <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        {s.location || "No location recorded"}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400">
                        <User className="w-3.5 h-3.5" />
                        {s.custodian
                          ? <>Custodian: <span className="text-gray-600 font-medium">{staffName(s.custodian)}</span></>
                          : "No custodian recorded"}
                      </div>
                    </div>
                  </div>

                  {isSuperAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEdit(s)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100"
                        title="Edit store"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50"
                        title="Delete store"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* What it is carrying */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-lg font-bold text-gray-900">{s.item_count ?? 0}</div>
                    <div className="text-[11px] text-gray-400 uppercase tracking-wider">Item lines</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-lg font-bold text-gray-900">{fmtQty(s.total_quantity ?? 0)}</div>
                    <div className="text-[11px] text-gray-400 uppercase tracking-wider">Units</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="text-lg font-bold text-gray-900">{naira(s.stock_value ?? 0)}</div>
                    <div className="text-[11px] text-gray-400 uppercase tracking-wider">Value</div>
                  </div>
                </div>

                {/* What you do to a store */}
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <button
                    onClick={() => setOpen((p) => ({ ...p, [s.id]: !p[s.id] }))}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    {isOpen ? "Hide stock" : "View stock"}
                  </button>

                  {/* Only the store's own custodian (or a super admin) works
                      it, so the rest see why rather than a row of buttons that
                      would be refused. */}
                  <AccessGate perm={PERMS.store.movement}>
                    {mine ? (
                      <>
                        <button
                          onClick={() => setReceiving(s.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          <Plus className="w-3.5 h-3.5" /> Receive
                        </button>
                        {/* Hub and spokes: the main store supplies the others,
                            the others send back to it. */}
                        <button
                          onClick={() => setAction({ action: "transfer", storeId: s.id })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                        >
                          <ArrowLeftRight className="w-3.5 h-3.5" />
                          {s.is_main ? "Send to a store" : "Send to main store"}
                        </button>
                        {/* The main store deals with stores, not with staff. */}
                        {!s.is_main && (
                          <>
                            <button
                              onClick={() => setAction({ action: "issue", storeId: s.id })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                            >
                              <PackageCheck className="w-3.5 h-3.5" /> Issue to staff
                            </button>
                            <button
                              onClick={() => setAction({ action: "return", storeId: s.id })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                            >
                              <Undo2 className="w-3.5 h-3.5" /> Take back
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">{refusal(s)}</span>
                    )}
                  </AccessGate>
                </div>
              </div>

              {isOpen && (
                <div className="border-t bg-gray-50/40 px-5 py-4">
                  <StoreShelf storeId={s.id} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* What is out with staff — stock that has left every store */}
      {(holdings as any[]).length > 0 && (
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <PackageCheck className="w-4 h-4 text-blue-600" />
            <h2 className="font-bold text-gray-800">Out with staff</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Issued out of a store and not yet sold or returned. This stock is not counted in
            any store's balance above.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b">
                  <th className="py-2 pr-3 font-medium text-xs uppercase tracking-wider">Holder</th>
                  <th className="py-2 px-3 font-medium text-xs uppercase tracking-wider">Item</th>
                  <th className="py-2 pl-3 font-medium text-xs uppercase tracking-wider text-right">Holding</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(holdings as any[]).map((h) => (
                  <tr key={h.id}>
                    <td className="py-2.5 pr-3 font-medium text-gray-900">{staffName(h.admin)}</td>
                    <td className="py-2.5 px-3 text-gray-700">
                      {h.item?.name}
                      <span className="text-xs text-gray-400 font-mono"> {h.item?.code}</span>
                    </td>
                    <td className="py-2.5 pl-3 text-right text-gray-800">
                      {fmtQty(h.quantity)} {h.item?.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / edit */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6">
          <form
            onSubmit={save}
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{form.id ? "Edit store" : "New store"}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={inputCls}
                  placeholder="e.g. National Secretariat Store"
                />
              </div>
              <div>
                <label className={labelCls}>Code *</label>
                <input
                  required
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  className={inputCls}
                  placeholder="MAIN"
                />
              </div>
              <div>
                <label className={labelCls}>Location</label>
                <input
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  className={inputCls}
                  placeholder="Abuja"
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Custodian</label>
              <select
                value={form.custodian_admin_id}
                onChange={(e) => setForm((f) => ({ ...f, custodian_admin_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">No custodian</option>
                {staff.map((a: any) => (
                  <option key={a.id} value={a.id}>{staffName(a)}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                The person answerable for this store. It does not restrict who may record
                movements.
              </p>
            </div>

            <div>
              <label className={labelCls}>Note</label>
              <input
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                className={inputCls}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              Active — stock can be moved in and out of it
            </label>

            {/* Only a super admin moves the hub, and only one store is it. */}
            {isSuperAdmin && (
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.is_main}
                  onChange={(e) => setForm((f) => ({ ...f, is_main: e.target.checked }))}
                />
                <span>
                  Main store
                  <span className="block text-xs text-gray-400">
                    The hub. It supplies the other stores and takes stock back from them;
                    the other stores deal with it and with staff, never with each other.
                    Making this one main takes the title from whichever store holds it now.
                  </span>
                </span>
              </label>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900">
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || updating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg disabled:opacity-40"
              >
                {(creating || updating) && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {receiving !== null && (
        <MovementModal type="in" storeId={receiving} onClose={() => setReceiving(null)} />
      )}
      {action && (
        <StockActionModal action={action.action} storeId={action.storeId} onClose={() => setAction(null)} />
      )}
    </div>
    </StoreShell>
  );
}

export default function StoresPage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <StoresPageInner />
    </PageGate>
  );
}
